import type { GridSortModel } from "@mui/x-data-grid"
import type { MandatesModel, MandateTableFilters } from "@/src/types/mandatesModel"
import type {
    FormSchemaMandateType,
    FormSchemaJobPostingType,
} from "@/src/features/jobPosting/schemas/jobPostingFormSchema"

import { z } from "zod"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"
import { EDIT_MANDATE_STORAGE_KEY } from "@/src/utils/constants"
import { updateJobRound } from "@/src/services/jobRound"
// COMMENTED OUT: JOB_ROUND_GENERATE API approach is being removed
// import { generateJobRounds } from "@/src/services/jobRound"
import { getMandate, createJobDraft, editMandateForm, activateDraftMandate } from "@/src/services/mandates"
// Import schemas
import {
    FormSchemaMandate0,
    FormSchemaMandate1,
    FormSchemaMandate2,
    FormSchemaMandate3,
} from "@/src/features/jobPosting/schemas/jobPostingFormSchema"

import { useAuthStore } from "./auth.slice"

import type { BaseStoreState, PaginationState } from "../types"

// Create combined schema for complete form validation

// Store state interface
export interface MandatesStoreState extends BaseStoreState {
    mandates: MandatesModel[]
    currentMandate: MandatesModel | null
    formData: FormSchemaJobPostingType & { isDefault: boolean }
    filters: MandateTableFilters
    pagination: PaginationState
    sortModel: GridSortModel
    searchQuery: string
    tabData: { [key: string]: number } | undefined
    currentStep: number
    isDraft: boolean
}

// Store actions interface
export interface MandatesStoreActions {
    // Form management
    setFormData: (data: Partial<FormSchemaJobPostingType>) => void
    validateFormStep: (step: number) => Promise<boolean>
    saveMandateDraft: () => Promise<void>
    submitMandate: () => Promise<void>
    resetForm: () => void
    setCurrentStep: (step: number) => void
    setIsDraft: (isDraft: boolean) => void

    // Mandate management
    fetchMandates: (filters?: MandateTableFilters) => Promise<void>
    setCurrentMandate: (id: string) => Promise<void>
    createMandate: (data: FormSchemaMandateType) => Promise<void>
    updateMandate: (id: string, data: Partial<FormSchemaMandateType>) => Promise<void>
    deleteMandate: (id: string) => Promise<void>

    // Filters and pagination
    updateFilters: (filters: Partial<MandateTableFilters>) => void
    setPagination: (pagination: Partial<PaginationState>) => void
    setSortModel: (sortModel: GridSortModel) => void
    setSearchQuery: (query: string) => void
    setTabData: (tabData: { [key: string]: number }) => void

    // Round management
    createRound: (roundData: any) => Promise<void>
    updateRound: (roundId: string, roundData: any) => Promise<void>

    // State management
    resetState: () => void
}

export type MandatesStore = MandatesStoreState & MandatesStoreActions

// Default form values
const defaultFormValues: FormSchemaMandateType & { isDefault: boolean } = {
    jobDescriptionPrompt: "",
    jdFile: null,
    jdFileLink: null,
    clientId: "",
    title: "",
    jobCity: "",
    jobArea: "",
    experienceRequired: "",
    experiencedCandidateDefinition: "",
    fresherCandidateDefinition: "",
    workType: "",
    workMode: "",
    workScheduleAndShiftDetails: "",
    experiencedCompensationDetails: "",
    freshersCompensationDetails: "",
    additionalDetails: "",
    interviewProcess: "",
    rounds: [],
    candidateFaqPairs: [],
    candidateFaqFileName: undefined,
    candidateFaqFileUrl: undefined,
    isDefault: true,
}

export const useMandatesStore = create<MandatesStore>()(
    persist(
        immer((set, get) => ({
            // State
            mandates: [],
            currentMandate: null,
            formData: defaultFormValues,
            filters: {
                mandate: undefined,
                clientId: undefined,
                agentExists: undefined,
                status: undefined,
            },
            pagination: { page: 0, pageSize: 40 },
            sortModel: [],
            searchQuery: "",
            tabData: undefined,
            currentStep: 0,
            isDraft: false,
            loading: false,
            error: null,

            // Actions
            setFormData: (data: Partial<FormSchemaJobPostingType>) => {
                set((state) => {
                    state.formData = { ...state.formData, ...data, isDefault: false }
                    state.error = null
                })
            },

            validateFormStep: async (step: number): Promise<boolean> => {
                const { formData } = get()

                try {
                    let schema: z.ZodSchema<any>

                    switch (step) {
                        case 0:
                            schema = FormSchemaMandate0
                            break
                        case 1:
                            schema = FormSchemaMandate1
                            break
                        case 2:
                            schema = FormSchemaMandate2
                            break
                        case 3:
                            schema = FormSchemaMandate3
                            break
                        default:
                            throw new Error(`Invalid step: ${step}`)
                    }

                    // Direct validation without middleware
                    const result = schema.safeParse(formData)
                    if (!result.success) {
                        set((state) => {
                            state.error = "Form validation failed"
                        })
                        return false
                    }

                    return true
                } catch (error) {
                    set((state) => {
                        state.error = error instanceof Error ? error.message : "Validation failed"
                    })
                    return false
                }
            },

            saveMandateDraft: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const { formData } = get()
                    const { user } = useAuthStore.getState()

                    if (!user?.activeWorkspace?.id) {
                        throw new Error("No active workspace found")
                    }

                    // Form data validation can be added later if needed

                    set((state) => {
                        state.isDraft = true
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to save draft"
                    })
                    throw error
                }
            },

            submitMandate: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const { formData } = get()
                    const { user } = useAuthStore.getState()

                    if (!user?.activeWorkspace?.id) {
                        throw new Error("No active workspace found")
                    }

                    // Complete form validation can be added later if needed

                    set((state) => {
                        state.isDraft = false
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to submit mandate"
                    })
                    throw error
                }
            },

            resetForm: () => {
                set((state) => {
                    state.formData = { ...defaultFormValues }
                    state.currentStep = 0
                    state.isDraft = false
                    state.error = null
                })
            },

            setCurrentStep: (step: number) => {
                set((state) => {
                    state.currentStep = step
                })
            },

            setIsDraft: (isDraft: boolean) => {
                set((state) => {
                    state.isDraft = isDraft
                })
            },

            fetchMandates: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const { pagination, sortModel } = get()
                    const { user } = useAuthStore.getState()

                    if (!user?.activeWorkspace?.id) {
                        throw new Error("No active workspace found")
                    }

                    // This would typically call an API to fetch mandates
                    // For now, we'll set empty array as placeholder
                    const mandates: MandatesModel[] = []

                    set((state) => {
                        state.mandates = mandates
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to fetch mandates"
                    })
                }
            },

            setCurrentMandate: async (id: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const mandate = await getMandate(id)

                    set((state) => {
                        state.currentMandate = mandate.data || null
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to fetch mandate"
                    })
                }
            },

            createMandate: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const { user } = useAuthStore.getState()

                    if (!user?.activeWorkspace?.id) {
                        throw new Error("No active workspace found")
                    }

                    // Data validation can be added later if needed

                    set((state) => {
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to create mandate"
                    })
                    throw error
                }
            },

            updateMandate: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    set((state) => {
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to update mandate"
                    })
                    throw error
                }
            },

            deleteMandate: async (id: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to delete mandate
                    // For now, we'll just remove from local state

                    set((state) => {
                        state.mandates = state.mandates.filter((m) => m.id !== id)
                        if (state.currentMandate?.id === id) {
                            state.currentMandate = null
                        }
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to delete mandate"
                    })
                    throw error
                }
            },

            updateFilters: (filters: Partial<MandateTableFilters>) => {
                set((state) => {
                    state.filters = { ...state.filters, ...filters }
                    // Reset pagination to first page when filters change to avoid showing empty results
                    // This ensures users always see data after applying filters
                    state.pagination.page = 0
                })
            },

            setPagination: (pagination: Partial<PaginationState>) => {
                set((state) => {
                    state.pagination = { ...state.pagination, ...pagination }
                })
            },

            setSortModel: (sortModel: GridSortModel) => {
                set((state) => {
                    state.sortModel = [...sortModel]
                })
            },

            setSearchQuery: (query: string) => {
                set((state) => {
                    state.searchQuery = query
                })
            },

            setTabData: (tabData: { [key: string]: number }) => {
                set((state) => {
                    state.tabData = tabData
                })
            },

            createRound: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const { currentMandate } = get()

                    if (!currentMandate?.id) {
                        throw new Error("No current mandate selected")
                    }

                    set((state) => {
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to create round"
                    })
                    throw error
                }
            },

            updateRound: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    set((state) => {
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to update round"
                    })
                    throw error
                }
            },

            // COMMENTED OUT: JOB_ROUND_GENERATE API approach is being removed
            // generateRounds: async (jobId: string) => {
            //     set((state) => {
            //         state.loading = true
            //         state.error = null
            //     })

            //     try {
            //         const response = await generateJobRounds(jobId)

            //         set((state) => {
            //             state.loading = false
            //             state.error = null
            //         })
            //     } catch (error) {
            //         set((state) => {
            //             state.loading = false
            //             state.error = error instanceof Error ? error.message : "Failed to generate rounds"
            //         })
            //         throw error
            //     }
            // },

            resetState: () => {
                set((state) => {
                    // Reset to initial state (excluding formData which has its own reset)
                    state.mandates = []
                    state.currentMandate = null
                    state.filters = {
                        mandate: undefined,
                        clientId: undefined,
                        agentExists: undefined,
                        status: undefined,
                    }
                    state.pagination = { page: 0, pageSize: 40 }
                    state.sortModel = []
                    state.searchQuery = ""
                    state.tabData = undefined
                    state.loading = false
                    state.error = null
                })
            },
        })),
        {
            name: EDIT_MANDATE_STORAGE_KEY,
            partialize: (state) => ({
                formData: state.formData,
                currentStep: state.currentStep,
                isDraft: state.isDraft,
                filters: state.filters,
                searchQuery: state.searchQuery,
                pagination: state.pagination,
                sortModel: state.sortModel,
            }),
        }
    )
)
